import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { PersonCard } from "./PersonCard";
import { QuickConnectDialog } from "./QuickConnectDialog";
import { NamePackDialog } from "./NamePackDialog";
import { setCustomNames } from "../lib/nameGender";
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator,
  ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger,
} from "./ui/context-menu";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Plus, Palette, X, Heart, Info, Users, Baby, UserPlus, Trash2, Eye, EyeOff, RotateCw, UsersRound, GitBranch, Pencil as PencilIcon } from "lucide-react";
import { api, audioUrl } from "../lib/api";
import { computeGenerations, buildLayout, computeLineageRoots, descendantSet, THEME_DIMS, kinshipLabel } from "../lib/layout";
import { MIN_SCALE, MAX_SCALE, PALETTE, clamp, hexA } from "./tree/treeConstants";
import { TreeMinimap } from "./tree/TreeMinimap";
import { TreeControls } from "./tree/TreeControls";
import { TourCaption, PresenterBar } from "./tree/TourOverlay";
import { ColorPickerDialog, ThemesDialog, LegendExportDialog } from "./tree/TreeDialogs";
import { GroupsPanel } from "./tree/GroupsPanel";
import { TreeSearch, PathBanner, MultiSelectBar, FrameModeHint, FocusLegend } from "./tree/TreeBanners";

function buildRadial(focusId, byId, CW, CH) {
  const ring = 240; const nodes = {};
  nodes[focusId] = { x: -CW / 2, y: -CH / 2 };
  const place = (id, gen, a0, a1) => {
    const parents = (byId[id]?.parent_ids || []).filter((p) => byId[p]).slice(0, 2);
    if (!parents.length || gen > 4) return;
    const span = (a1 - a0) / parents.length;
    parents.forEach((pid, i) => {
      const pa0 = a0 + i * span, pa1 = a0 + (i + 1) * span;
      const ang = (pa0 + pa1) / 2, r = gen * ring;
      if (!(pid in nodes)) nodes[pid] = { x: r * Math.cos(ang) - CW / 2, y: r * Math.sin(ang) - CH / 2 };
      place(pid, gen + 1, pa0, pa1);
    });
  };
  place(focusId, 1, Math.PI, 2 * Math.PI);
  return nodes;
}

// undirected relationship graph for path highlighting
function buildAdjacency(members) {
  const adj = {};
  const add = (a, b) => { if (!a || !b) return; (adj[a] = adj[a] || new Set()).add(b); (adj[b] = adj[b] || new Set()).add(a); };
  members.forEach((m) => {
    (m.parent_ids || []).forEach((p) => add(m.id, p));
    (m.spouse_ids || []).forEach((s) => add(m.id, s));
  });
  return adj;
}
function shortestPath(adj, a, b) {
  if (!a || !b || a === b) return a && a === b ? [a] : [];
  const prev = { [a]: null }; const q = [a];
  while (q.length) {
    const cur = q.shift();
    if (cur === b) break;
    (adj[cur] ? [...adj[cur]] : []).forEach((n) => { if (!(n in prev)) { prev[n] = cur; q.push(n); } });
  }
  if (!(b in prev)) return [];
  const path = []; let c = b; while (c != null) { path.unshift(c); c = prev[c]; }
  return path;
}

export function FamilyTree({ members, onSelect, selectedId, onChanged, canEdit = false, isAdmin = false, bgUrl = null, onBgChanged, onAddRelative, lineageColors = {}, onSettingsChanged, groups = [], onGroupsChanged, frames = [], onFramesChanged, familyName = "", homeId = null, onRestoreSelection, narrationVoice = "fable" }) {
  const viewportRef = useRef(null);
  const innerRef = useRef(null);
  const tRef = useRef({ scale: 1, x: 0, y: 0 });
  const panRef = useRef({ x: 0, y: 0, active: false });
  const suppressCtxRef = useRef(false);
  const ndRef = useRef(null);
  const dropRef = useRef(null);
  const bgFileRef = useRef(null);
  const contentRef = useRef({ w: 1, h: 1 });
  const mmRef = useRef(null);
  const mmDragRef = useRef(false);
  const shiftRef = useRef({ x: 0, y: 0 });
  const dispRef = useRef({});

  const [t, setT] = useState({ scale: 1, x: 0, y: 0 });
  const [nodeDrag, setNodeDrag] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [colorBy, setColorBy] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [theme, setTheme] = useState(() => localStorage.getItem("familyroots_theme") || "classic");
  const [orientation, setOrientation] = useState(() => localStorage.getItem("familyroots_orient") || "down");
  const [showBands, setShowBands] = useState(() => localStorage.getItem("familyroots_bands") === "1");
  const [showGroups, setShowGroups] = useState(false);
  const [showMinimap, setShowMinimap] = useState(false);
  const [bloodlineOnly, setBloodlineOnly] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [focusGroups, setFocusGroups] = useState(() => new Set());
  const [pathMode, setPathMode] = useState(false);
  const [pathSel, setPathSel] = useState([]); // up to 2 member ids
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [multiSel, setMultiSel] = useState(() => new Set());
  const [colorPicker, setColorPicker] = useState(null); // { ids:[], title } | null
  const [quickConnect, setQuickConnect] = useState(null); // { anchor, relation, existing } | null
  const [themes, setThemes] = useState([]);
  const [themePreview, setThemePreview] = useState(null); // { map } | null
  const [themesOpen, setThemesOpen] = useState(false);
  const [namePacksOpen, setNamePacksOpen] = useState(false);
  const [legendDialog, setLegendDialog] = useState(null); // { asPdf } | null
  const [legendTitle, setLegendTitle] = useState(() => localStorage.getItem("familyroots_legend_title") || "");
  const [legendDate, setLegendDate] = useState(() => new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }));
  const [legendNote, setLegendNote] = useState(() => localStorage.getItem("familyroots_legend_note") || "");
  const [frameMode, setFrameMode] = useState(false);
  const [drawFrame, setDrawFrame] = useState(null); // canonical {x,y,w,h} while drawing
  const [marquee, setMarquee] = useState(null); // display {x0,y0,x1,y1}
  const [frameDrag, setFrameDrag] = useState(null); // {id,x,y,w,h} preview
  const frameDrawRef = useRef(null);
  const marqueeRef = useRef(null);
  const frameEditRef = useRef(null);
  const didFitRef = useRef(false);
  const animRef = useRef(null);
  const spaceRef = useRef(false);
  const [spaceDown, setSpaceDown] = useState(false);
  // Presenting lock + guided tour + find-me
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  const [tour, setTour] = useState(null); // { i } while playing, else null
  const [tourFocusId, setTourFocusId] = useState(null);
  const [tourPaused, setTourPaused] = useState(false);
  const [tourVoice, setTourVoice] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const tourRef = useRef(null);
  const tourTimer = useRef(null);
  const audioRef = useRef(null);
  const [mmHover, setMmHover] = useState(null); // name shown in overview header on hover
  useEffect(() => { lockedRef.current = locked; }, [locked]);
  useEffect(() => { tourRef.current = tour; }, [tour]);
  const stopAudio = useCallback(() => { if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } audioRef.current = null; } }, []);
  const stopTour = useCallback(() => {
    if (tourTimer.current) { clearTimeout(tourTimer.current); tourTimer.current = null; }
    stopAudio(); setTour(null); setTourFocusId(null); setTourPaused(false);
  }, [stopAudio]);

  useEffect(() => { tRef.current = t; }, [t]);
  useEffect(() => { localStorage.setItem("familyroots_theme", theme); }, [theme]);
  useEffect(() => { localStorage.setItem("familyroots_orient", orientation); }, [orientation]);
  useEffect(() => { localStorage.setItem("familyroots_bands", showBands ? "1" : "0"); }, [showBands]);

  const reloadThemes = useCallback(async () => { try { const r = await api.listThemes(); setThemes(r.themes || []); } catch { /* not signed in */ } }, []);
  useEffect(() => { if (canEdit) reloadThemes(); }, [canEdit, reloadThemes]);

  const reloadNamePacks = useCallback(async () => {
    try {
      const r = await api.listNamePacks();
      const map = {};
      (r.names || []).forEach((n) => { if (n.name && n.gender) map[n.name] = n.gender; });
      setCustomNames(map);
    } catch { /* not signed in */ }
  }, []);
  useEffect(() => { if (canEdit) reloadNamePacks(); }, [canEdit, reloadNamePacks]);

  const dims = THEME_DIMS[theme] || THEME_DIMS.classic;
  const CW = dims.cardW, CH = dims.cardH;
  // Locked-by-default: only real editors who have flipped the "Edit tree" toggle can rearrange/connect.
  const editing = canEdit && editMode;

  const isRadial = orientation === "radial";
  const radialFocusDep = isRadial ? selectedId : null;

  // Heavy layout (positions, generations, lineage roots) computed ONCE and reused.
  // Deliberately excludes the zoom/pan transform `t` so gestures never rebuild the tree.
  const base = useMemo(() => {
    const byId = Object.fromEntries(members.map((m) => [m.id, m]));
    const bloodPrimary = new Set();
    members.forEach((m) => { const fp = (m.parent_ids || []).find((p) => byId[p]); if (fp) bloodPrimary.add(fp); });
    const radialFocus = isRadial ? (byId[radialFocusDep] ? radialFocusDep : (members[0]?.id || null)) : null;
    const hidden = new Set();
    collapsed.forEach((id) => { if (byId[id]) descendantSet(members, id).forEach((d) => hidden.add(d)); });
    let vis, layout;
    if (isRadial && radialFocus) {
      const rnodes = buildRadial(radialFocus, byId, CW, CH);
      vis = members.filter((m) => m.id in rnodes);
      layout = { nodes: rnodes, width: 1, height: 1 };
    } else {
      vis = members.filter((m) => !hidden.has(m.id));
      layout = buildLayout(vis, computeGenerations(vis), dims);
    }
    const roots = computeLineageRoots(members);
    const uniqueRoots = [...new Set(Object.values(roots))];
    const rootIndex = Object.fromEntries(uniqueRoots.map((r, i) => [r, i]));
    return { byId, bloodPrimary, vis, layout, roots, rootIndex, radialFocus, uniqueRoots };
  }, [members, collapsed, isRadial, radialFocusDep, CW, CH, dims]);
  const { byId, bloodPrimary, vis, layout, roots, rootIndex, radialFocus, uniqueRoots } = base;

  // Married-in = no parents in this tree, has a spouse here, and isn't anyone's primary blood parent.
  const isMarriedIn = (m) => (m.parent_ids || []).filter((p) => byId[p]).length === 0 && (m.spouse_ids || []).some((s) => byId[s]) && !bloodPrimary.has(m.id);
  const colorFor = (id) => themePreview?.map?.[id] || byId[id]?.color || lineageColors[roots[id]] || PALETTE[(rootIndex[roots[id]] ?? 0) % PALETTE.length];
  const hiddenCountFor = (id) => descendantSet(members, id).size;

  // ---------- orientation transform (canonical <-> display) ----------
  // Also memoized: only re-derived when layout/orientation/node-drag change, never on zoom/pan.
  const display = useMemo(() => {
    const getPos = (id) => {
      if (nodeDrag) {
        if (nodeDrag.map && nodeDrag.map[id]) return nodeDrag.map[id];
        if (nodeDrag.id === id) return nodeDrag;
      }
      return layout.nodes[id];
    };
    let cminY = Infinity, cmaxY = -Infinity;
    vis.forEach((m) => { const p = getPos(m.id); if (!p) return; cminY = Math.min(cminY, p.y); cmaxY = Math.max(cmaxY, p.y + CH); });
    if (!isFinite(cminY)) { cminY = 0; cmaxY = CH; }
    const K = cminY + cmaxY;
    const fwdRaw = (p) => {
      switch (orientation) {
        case "up": return { x: p.x, y: K - p.y - CH };
        case "right": return { x: p.y, y: p.x };
        case "left": return { x: K - p.y - CH, y: p.x };
        default: return { x: p.x, y: p.y }; // down & radial (positions already final)
      }
    };
    const invRaw = (d) => {
      switch (orientation) {
        case "up": return { x: d.x, y: K - d.y - CH };
        case "right": return { x: d.y, y: d.x };
        case "left": return { x: d.y, y: K - d.x - CH };
        default: return { x: d.x, y: d.y };
      }
    };
    let dminX = Infinity, dminY = Infinity, dmaxX = -Infinity, dmaxY = -Infinity;
    vis.forEach((m) => { const p = getPos(m.id); if (!p) return; const d = fwdRaw(p); dminX = Math.min(dminX, d.x); dminY = Math.min(dminY, d.y); dmaxX = Math.max(dmaxX, d.x + CW); dmaxY = Math.max(dmaxY, d.y + CH); });
    if (!isFinite(dminX)) { dminX = 0; dminY = 0; dmaxX = CW; dmaxY = CH; }
    const shift = { x: dminX, y: dminY };
    const content = { w: (dmaxX - dminX) || 1, h: (dmaxY - dminY) || 1 };
    const disp = (id) => { const p = getPos(id); if (!p) return null; const d = fwdRaw(p); return { x: d.x - dminX, y: d.y - dminY }; };
    const dispPoint = (p) => { const d = fwdRaw(p); return { x: d.x - dminX, y: d.y - dminY }; };
    const dmap = {}; vis.forEach((m) => { const d = disp(m.id); if (d) dmap[m.id] = d; });
    return { invRaw, shift, content, disp, dispPoint, dmap };
  }, [vis, layout, orientation, nodeDrag, CW, CH]);
  const { invRaw, disp, dispPoint, dmap } = display;
  shiftRef.current = display.shift;
  contentRef.current = display.content;
  dispRef.current = dmap;

  const isVertical = orientation === "down" || orientation === "up";

  // ---------- zoom / pan ----------
  const clampT = useCallback((x, y, scale) => {
    const vp = viewportRef.current;
    if (!vp) return { x: 0, y: 0 };
    const vw = vp.clientWidth, vh = vp.clientHeight;
    const cw = (contentRef.current.w || 1) * scale;
    const ch = (contentRef.current.h || 1) * scale;
    // Free-roam: let the tree be dragged a long way past the people onto open
    // space (up to ~1.5 screens of empty space beyond the content in every
    // direction). Bounds stay FINITE so the transform can never run away to
    // Infinity/NaN — that runaway was the old edge freeze / blank-screen crash.
    // The "Back to tree" button + overview map are what keep you oriented.
    const roamX = vw * 1.5, roamY = vh * 1.5;
    const loX = Math.min(0, vw - cw) - roamX, hiX = Math.max(0, vw - cw) + roamX;
    const loY = Math.min(0, vh - ch) - roamY, hiY = Math.max(0, vh - ch) + roamY;
    let nx = clamp(x, loX, hiX);
    let ny = clamp(y, loY, hiY);
    if (!Number.isFinite(nx)) nx = (vw - cw) / 2;
    if (!Number.isFinite(ny)) ny = Math.max(48, (vh - ch) / 2);
    return { x: nx, y: ny };
  }, []);

  // Fraction of the smaller of {viewport, content} that is currently on screen.
  // Used to (a) light up "Back to tree" when you've drifted off, and (b) avoid
  // reopening the tree onto empty space when restoring a saved view.
  const overlapFrac = useCallback((x, y, scale) => {
    const vp = viewportRef.current; if (!vp) return 1;
    const vw = vp.clientWidth, vh = vp.clientHeight;
    const cw = (contentRef.current.w || 1) * scale, ch = (contentRef.current.h || 1) * scale;
    const ox = Math.max(0, Math.min(vw, x + cw) - Math.max(0, x));
    const oy = Math.max(0, Math.min(vh, y + ch) - Math.max(0, y));
    const ref = Math.min(cw * ch, vw * vh) || 1;
    const f = (ox * oy) / ref;
    return Number.isFinite(f) ? f : 1;
  }, []);

  const zoomAt = useCallback((factor, cx, cy) => {
    setT((prev) => {
      const ps = prev?.scale ?? 1, px = prev?.x ?? 0, py = prev?.y ?? 0;
      const ns = clamp(ps * factor, MIN_SCALE, MAX_SCALE);
      if (!Number.isFinite(ns) || ns <= 0) return prev;
      const k = ns / ps;
      return { scale: ns, ...clampT(cx - (cx - px) * k, cy - (cy - py) * k, ns) };
    });
  }, [clampT]);

  const cancelAnim = useCallback(() => { if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; } }, []);
  const zoomCenter = useCallback((factor) => { cancelAnim(); const vp = viewportRef.current; if (!vp) return; zoomAt(factor, vp.clientWidth / 2, vp.clientHeight / 2); }, [zoomAt, cancelAnim]);

  // Cancel any in-flight glide the moment the user takes over (pan/zoom/drag/key).
  // Smoothly glide the transform to a target {scale,x,y}. Instant if reduced-motion.
  const animateTo = useCallback((target, ms = 460) => {
    const vp = viewportRef.current; if (!vp || !target) return;
    cancelAnim();
    const end = { scale: target.scale, ...clampT(target.x, target.y, target.scale) };
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const start = tRef.current || { scale: 1, x: 0, y: 0 };
    if (reduce || ms <= 0 || !Number.isFinite(start.x) || !Number.isFinite(start.y)) { setT(end); return; }
    const t0 = performance.now();
    const ease = (p) => 1 - Math.pow(1 - p, 3); // easeOutCubic
    const step = (now) => {
      const p = Math.min(1, (now - t0) / ms), k = ease(p);
      setT({ scale: start.scale + (end.scale - start.scale) * k, x: start.x + (end.x - start.x) * k, y: start.y + (end.y - start.y) * k });
      animRef.current = p < 1 ? requestAnimationFrame(step) : null;
    };
    animRef.current = requestAnimationFrame(step);
  }, [clampT, cancelAnim]);

  const fit = useCallback((animate = true) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const pad = 100;
    const cw = Math.max(1, contentRef.current.w), ch = Math.max(1, contentRef.current.h);
    let s = Math.min((vp.clientWidth - pad) / cw, (vp.clientHeight - pad) / ch, 1);
    if (!Number.isFinite(s) || s <= 0) s = 0.5;
    s = clamp(s, MIN_SCALE, MAX_SCALE);
    const target = { scale: s, x: (vp.clientWidth - cw * s) / 2, y: Math.max(48, (vp.clientHeight - ch * s) / 2) };
    if (animate) animateTo(target); else setT(target);
  }, [animateTo]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    // Coalesce rapid wheel events into one update per animation frame so a fast
    // scroll / trackpad flick can't queue more zoom/pan work than the browser can draw.
    let raf = null;
    let zAccum = 0;          // accumulated zoom delta (pixels)
    let zx = 0, zy = 0;      // latest zoom focal point
    let panX = 0, panY = 0;  // accumulated pan delta
    const flush = () => {
      raf = null;
      if (zAccum !== 0) {
        zoomAt(Math.exp(-zAccum * 0.0015), zx, zy);
        zAccum = 0;
      }
      if (panX !== 0 || panY !== 0) {
        const dx = panX, dy = panY;
        panX = 0; panY = 0;
        setT((prev) => ({ ...prev, ...clampT((prev?.x ?? 0) - dx, (prev?.y ?? 0) - dy, prev?.scale ?? 1) }));
      }
    };
    const onWheel = (e) => {
      e.preventDefault();
      if (lockedRef.current) return;
      if (tourRef.current) stopTour();
      cancelAnim();
      const rect = vp.getBoundingClientRect?.();
      if (!rect) return;
      // Pinch (trackpad) or ctrl+wheel = zoom; plain wheel / two-finger scroll = pan (prevents zoom-to-blank).
      if (e.ctrlKey || e.metaKey) {
        zAccum += e.deltaY;
        zx = e.clientX - rect.left;
        zy = e.clientY - rect.top;
      } else {
        panX += e.deltaX;
        panY += e.deltaY;
      }
      if (raf == null) raf = requestAnimationFrame(flush);
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => { vp.removeEventListener("wheel", onWheel); if (raf != null) cancelAnimationFrame(raf); };
  }, [zoomAt, clampT, cancelAnim, stopTour]);

  // Safety watchdog: if the transform ever becomes invalid, snap back to a sensible fit.
  useEffect(() => {
    if (!Number.isFinite(t.x) || !Number.isFinite(t.y) || !Number.isFinite(t.scale) || t.scale <= 0) fit(false);
  }, [t, fit]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName) || e.target.isContentEditable) return;
      if (lockedRef.current) return;
      if (e.key === " " || e.code === "Space") { if (!spaceRef.current) { spaceRef.current = true; setSpaceDown(true); } e.preventDefault(); return; }
      if (tourRef.current && (e.key.startsWith("Arrow") || e.key === "+" || e.key === "=" || e.key === "-" || e.key === "_")) stopTour();
      if (e.key === "+" || e.key === "=") zoomCenter(1.2);
      else if (e.key === "-" || e.key === "_") zoomCenter(1 / 1.2);
      else if (e.key === "0") fit();
      else if (e.key.startsWith("Arrow")) {
        e.preventDefault(); cancelAnim();
        const stepPx = e.shiftKey ? 260 : 90;
        const dx = e.key === "ArrowLeft" ? stepPx : e.key === "ArrowRight" ? -stepPx : 0;
        const dy = e.key === "ArrowUp" ? stepPx : e.key === "ArrowDown" ? -stepPx : 0;
        if (dx || dy) setT((prev) => ({ ...prev, ...clampT((prev?.x ?? 0) + dx, (prev?.y ?? 0) + dy, prev?.scale ?? 1) }));
      }
    };
    const onKeyUp = (e) => { if (e.key === " " || e.code === "Space") { spaceRef.current = false; setSpaceDown(false); } };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("keyup", onKeyUp); };
  }, [zoomCenter, fit, clampT, cancelAnim, stopTour]);

  useEffect(() => {
    api.getTreePrefs().then((p) => {
      setPrefs(p || {});
      if (canEdit && p?.mode === "edit") setEditMode(true);
      if (canEdit && !p?.hint_dismissed) setShowHint(true);
    }).catch(() => {}).finally(() => setPrefsLoaded(true));
  }, [canEdit]);

  // Restore the last view (or fit as a safety net) once members + prefs are ready.
  useEffect(() => {
    if (!members.length || didFitRef.current || !prefsLoaded) return;
    didFitRef.current = true;
    const v = prefs;
    if (v && Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.scale) && v.scale > 0) {
      const sc = clamp(v.scale, MIN_SCALE, MAX_SCALE);
      const pos = clampT(v.x, v.y, sc);
      // Don't reopen onto empty space: if the saved view would hide the tree, fit instead.
      if (overlapFrac(pos.x, pos.y, sc) >= 0.1) setT({ scale: sc, ...pos });
      else fit(false);
    } else fit(false);
    // Reopen with the relative you were last looking at already selected (highlight only, no sheet).
    if (v && v.selected_id && members.some((m) => m.id === v.selected_id)) onRestoreSelection?.(v.selected_id);
    /* eslint-disable-next-line */
  }, [members.length, prefsLoaded]);

  // Remember mode + approximate view + last-viewed person on the account (debounced).
  useEffect(() => {
    if (!prefsLoaded) return;
    const id = setTimeout(() => {
      api.saveTreePrefs({ mode: editMode ? "edit" : "view", x: Math.round(t.x), y: Math.round(t.y), scale: +Number(t.scale).toFixed(3), selected_id: selectedId || "" }).catch(() => {});
    }, 900);
    return () => clearTimeout(id);
  }, [t, editMode, selectedId, prefsLoaded]);

  const dismissHint = () => { setShowHint(false); api.saveTreePrefs({ hint_dismissed: true }).catch(() => {}); };
  // Re-fit only when the user actually changes card style / direction — never on the
  // initial mount, so the restored "reopen where you left" view isn't clobbered.
  const orientFitRef = useRef(`${theme}|${orientation}`);
  useEffect(() => {
    const key = `${theme}|${orientation}`;
    if (orientFitRef.current === key) return;
    orientFitRef.current = key;
    if (members.length) fit();
    /* eslint-disable-next-line */
  }, [theme, orientation, members.length]);
  // Leaving edit mode clears any in-progress editing UI (frame tool, multi-selection).
  useEffect(() => { if (!editing) { setFrameMode(false); setMultiSel(new Set()); } }, [editing]);

  // pointer (screen) -> display coords (null when the canvas hasn't measured itself yet)
  const displayPoint = (e) => {
    const rect = viewportRef.current?.getBoundingClientRect?.();
    if (!rect) return null;
    const cur = tRef.current || { scale: 1, x: 0, y: 0 };
    const sc = cur.scale || 1;
    return { x: (e.clientX - rect.left - (cur.x ?? 0)) / sc, y: (e.clientY - rect.top - (cur.y ?? 0)) / sc };
  };
  // pointer (screen) -> canonical coords
  const canonicalPoint = (e) => {
    const d = displayPoint(e);
    if (!d) return null;
    return invRaw({ x: d.x + (shiftRef.current?.x ?? 0), y: d.y + (shiftRef.current?.y ?? 0) });
  };
  // Snapshot the pan anchor NOW so a later pointerup/reset can't null it out before the updater runs.
  const startPan = (e) => { panRef.current = { x: e.clientX - (t?.x ?? 0), y: e.clientY - (t?.y ?? 0), active: true }; };
  const endPan = () => { panRef.current = { x: 0, y: 0, active: false }; };
  const panTo = (e) => {
    const pr = panRef.current;
    if (!pr?.active) return;
    const ax = pr.x ?? 0, ay = pr.y ?? 0, cx = e.clientX, cy = e.clientY;
    setT((prev) => ({ ...prev, ...clampT(cx - ax, cy - ay, prev?.scale ?? 1) }));
  };

  // ---------- node drag & drop ----------
  const hitTest = (p, excludeIds) => {
    for (const m of vis) {
      if (excludeIds.has(m.id)) continue;
      const n = layout.nodes[m.id];
      if (!n) continue;
      if (p.x >= n.x - 10 && p.x <= n.x + CW + 10 && p.y >= n.y - 10 && p.y <= n.y + CH + 10) return m.id;
    }
    return null;
  };

  const onNodePointerDown = (e, id) => {
    e.stopPropagation();
    if (e.button && e.button !== 0) return;
    cancelAnim();
    if (tourRef.current) stopTour();
    if (spaceRef.current) { startPan(e); return; }
    if (lockedRef.current) { ndRef.current = { id, viewTap: true, startClientX: e.clientX, startClientY: e.clientY, moved: false }; return; }
    if (!editing) {
      // View mode: dragging a card pans the canvas; a tap opens their profile. Nothing moves.
      startPan(e);
      ndRef.current = { id, viewTap: true, startClientX: e.clientX, startClientY: e.clientY, moved: false };
      return;
    }
    const base = layout.nodes[id];
    if (!base) return;
    const p = canonicalPoint(e);
    if (!p) return;
    const mods = e.shiftKey || e.ctrlKey || e.metaKey;
    const group = !mods && multiSel.has(id) && multiSel.size > 1;
    const ids = group ? [...multiSel].filter((x) => layout.nodes[x]) : [id];
    const origins = {}; ids.forEach((x) => { if (layout.nodes[x]) origins[x] = { ...layout.nodes[x] }; });
    ndRef.current = { id, ids, grabX: p.x - base.x, grabY: p.y - base.y, origins, startClientX: e.clientX, startClientY: e.clientY, moved: false, mods };
  };
  const onPointerDown = (e) => {
    cancelAnim();
    if (lockedRef.current) return;
    if (tourRef.current) stopTour();
    if (spaceRef.current && e.button === 0) { startPan(e); return; }
    if (frameMode && editing && e.button === 0) { frameDrawRef.current = canonicalPoint(e); setDrawFrame(null); return; }
    if (editing && (e.button === 2 || (e.button === 0 && e.shiftKey))) {
      const d = displayPoint(e);
      if (!d) return;
      marqueeRef.current = { x: d.x, y: d.y, right: e.button === 2 };
      setMarquee({ x0: d.x, y0: d.y, x1: d.x, y1: d.y });
      return;
    }
    if (e.button && e.button !== 0) return;
    if (multiSel.size) setMultiSel(new Set());
    startPan(e);
  };
  const onPointerMove = (e) => {
    if (!viewportRef.current) return;
    if (frameEditRef.current) {
      const fe = frameEditRef.current;
      const p = canonicalPoint(e);
      if (!p) return;
      fe.moved = true;
      if (fe.mode === "move") setFrameDrag({ id: fe.id, x: p.x - fe.grabX, y: p.y - fe.grabY, w: fe.w, h: fe.h });
      else setFrameDrag({ id: fe.id, x: fe.x, y: fe.y, w: Math.max(40, p.x - fe.x), h: Math.max(40, p.y - fe.y) });
      return;
    }
    if (frameDrawRef.current) {
      const s = frameDrawRef.current, p = canonicalPoint(e);
      if (!p) return;
      setDrawFrame({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
      return;
    }
    if (marqueeRef.current) {
      const s = marqueeRef.current, d = displayPoint(e);
      if (!d) return;
      setMarquee({ x0: s.x, y0: s.y, x1: d.x, y1: d.y });
      return;
    }
    if (ndRef.current) {
      const nd0 = ndRef.current;
      if (nd0.viewTap) {
        if (!nd0.moved && Math.hypot(e.clientX - nd0.startClientX, e.clientY - nd0.startClientY) >= 4) nd0.moved = true;
        panTo(e);
        return;
      }
      if (!editing) return;
      const nd = ndRef.current;
      if (!nd.moved && Math.hypot(e.clientX - nd.startClientX, e.clientY - nd.startClientY) < 5) return;
      const p = canonicalPoint(e);
      if (!p) return;
      nd.moved = true; nd.cx = e.clientX; nd.cy = e.clientY;
      if (nd.ids.length > 1) {
        const nx = p.x - nd.grabX, ny = p.y - nd.grabY;
        const o0 = nd.origins[nd.id];
        if (!o0) return;
        const dx = nx - o0.x, dy = ny - o0.y;
        const map = {};
        nd.ids.forEach((x) => { const o = nd.origins[x]; if (o) map[x] = { x: o.x + dx, y: o.y + dy }; });
        setNodeDrag({ id: nd.id, x: nx, y: ny, map, moved: true });
        return;
      }
      const exclude = descendantSet(members, nd.id); exclude.add(nd.id);
      const target = hitTest(p, exclude);
      dropRef.current = target; setDropTarget(target);
      setNodeDrag({ id: nd.id, x: p.x - nd.grabX, y: p.y - nd.grabY, moved: true });
      return;
    }
    panTo(e);
  };
  const finishNodeDrag = async () => {
    const nd = ndRef.current; ndRef.current = null;
    if (nd?.viewTap) { endPan(); if (!nd.moved) { setMultiSel(new Set()); onSelect(nd.id); } return; }
    const target = dropRef.current; dropRef.current = null;
    setDropTarget(null);
    if (!nd) return;
    if (!nd.moved) { setNodeDrag(null); handleNodeClick(nd.id, nd.mods); return; }
    // Clamp a dropped position so a card can never be flung far enough to blank the tree.
    const clampPos = (pos) => {
      const ns = Object.values(layout.nodes); if (!ns.length || !pos) return pos;
      const xs = ns.map((n) => n.x), ys = ns.map((n) => n.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
      const mx = Math.max(600, maxX - minX), my = Math.max(400, maxY - minY);
      return { x: clamp(pos.x, minX - mx, maxX + mx), y: clamp(pos.y, minY - my, maxY + my) };
    };
    // group move: save all positions
    if (nd.ids.length > 1) {
      const map = nodeDrag?.map;
      setNodeDrag(null);
      if (!canEdit || !map) return;
      try {
        await Promise.all(nd.ids.map((x) => { const cp = clampPos(map[x]); if (!cp) return null; return api.update(x, { pos_x: Math.round(cp.x), pos_y: Math.round(cp.y) }); }));
        onChanged?.();
      } catch { toast.error("Could not move the selection"); onChanged?.(); }
      return;
    }
    const el = nd.cx != null ? document.elementFromPoint(nd.cx, nd.cy) : null;
    const gEl = el && el.closest ? el.closest("[data-group-drop]") : null;
    if (gEl) { setNodeDrag(null); if (canEdit) await addToGroup(gEl.getAttribute("data-group-drop"), nd.id); return; }
    const finalPos = nodeDrag ? { x: nodeDrag.x, y: nodeDrag.y } : null;
    setNodeDrag(null);
    if (!canEdit) return;
    try {
      if (target) {
        // Drag one node onto another → let the user choose how they connect.
        openQuick(byId[target], null, byId[nd.id]);
      } else if (finalPos) {
        const cp = clampPos(finalPos);
        await api.update(nd.id, { pos_x: Math.round(cp.x), pos_y: Math.round(cp.y) });
        onChanged?.();
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update the tree"); onChanged?.(); }
  };

  const finishFrameDraw = async () => {
    const df = drawFrame; frameDrawRef.current = null; setDrawFrame(null);
    setFrameMode(false);
    if (!df || df.w < 24 || df.h < 24 || !canEdit) return;
    try {
      await api.createFrame({ x: Math.round(df.x), y: Math.round(df.y), w: Math.round(df.w), h: Math.round(df.h) });
      toast.success("Frame added — rename or recolour it any time");
      onFramesChanged?.();
    } catch { toast.error("Could not create the frame"); }
  };

  const finishMarquee = () => {
    const mq = marquee; marqueeRef.current = null; setMarquee(null);
    if (!mq) return;
    const minX = Math.min(mq.x0, mq.x1), maxX = Math.max(mq.x0, mq.x1);
    const minY = Math.min(mq.y0, mq.y1), maxY = Math.max(mq.y0, mq.y1);
    if (Math.abs(maxX - minX) < 8 && Math.abs(maxY - minY) < 8) return;
    const picked = new Set();
    vis.forEach((m) => {
      const p = disp(m.id); if (!p) return;
      if (p.x + CW >= minX && p.x <= maxX && p.y + CH >= minY && p.y <= maxY) picked.add(m.id);
    });
    setMultiSel(picked);
  };

  const finishFrameEdit = async () => {
    const fe = frameEditRef.current; frameEditRef.current = null;
    const fd = frameDrag; setFrameDrag(null);
    if (!fe || !fe.moved || !fd) return;
    try { await api.updateFrame(fe.id, { x: Math.round(fd.x), y: Math.round(fd.y), w: Math.round(fd.w), h: Math.round(fd.h) }); onFramesChanged?.(); }
    catch { toast.error("Could not update the frame"); }
  };

  const onPointerUp = () => {
    if (frameEditRef.current) { finishFrameEdit(); return; }
    if (frameDrawRef.current || drawFrame) { finishFrameDraw(); return; }
    if (marqueeRef.current || marquee) { if (marqueeRef.current?.right) suppressCtxRef.current = true; finishMarquee(); return; }
    if (ndRef.current) finishNodeDrag();
    endPan();
  };

  const handleNodeClick = (id, mods) => {
    if (pathMode) {
      setPathSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-2)));
      return;
    }
    if (mods) {
      setMultiSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
      return;
    }
    if (multiSel.size) setMultiSel(new Set());
    onSelect(id);
  };

  // frame helpers
  const getFrame = (f) => (frameDrag && frameDrag.id === f.id ? { ...f, ...frameDrag } : f);
  const onFrameBodyDown = (e, f) => {
    e.stopPropagation();
    if (!canEdit || e.button) return;
    const p = canonicalPoint(e);
    if (!p) return;
    frameEditRef.current = { id: f.id, mode: "move", grabX: p.x - f.x, grabY: p.y - f.y, moved: false };
  };
  const onFrameResizeDown = (e, f) => {
    e.stopPropagation();
    if (!canEdit || e.button) return;
    frameEditRef.current = { id: f.id, mode: "resize", x: f.x, y: f.y, moved: false };
  };
  const renameFrame = async (f) => { const label = window.prompt("Frame label", f.label); if (label == null) return; try { await api.updateFrame(f.id, { label: label.trim() || f.label }); onFramesChanged?.(); } catch { toast.error("Could not rename"); } };
  const recolorFrame = async (f, color) => { try { await api.updateFrame(f.id, { color }); onFramesChanged?.(); } catch { toast.error("Could not recolour"); } };
  const deleteFrameFn = async (f) => { if (!window.confirm(`Delete frame "${f.label}"?`)) return; try { await api.deleteFrame(f.id); onFramesChanged?.(); } catch { toast.error("Could not delete frame"); } };

  const frameSelection = async () => {
    const ids = [...multiSel].filter((x) => layout.nodes[x]);
    if (!ids.length) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ids.forEach((x) => { const n = layout.nodes[x]; if (!n) return; minX = Math.min(minX, n.x); minY = Math.min(minY, n.y); maxX = Math.max(maxX, n.x + CW); maxY = Math.max(maxY, n.y + CH); });
    if (!Number.isFinite(minX)) return;
    const pad = 28;
    try {
      await api.createFrame({ x: Math.round(minX - pad), y: Math.round(minY - pad), w: Math.round(maxX - minX + pad * 2), h: Math.round(maxY - minY + pad * 2) });
      toast.success("Framed the selection");
      onFramesChanged?.();
    } catch { toast.error("Could not create the frame"); }
  };

  const navMinimap = (e, animate = false) => {
    const el = mmRef.current, vp = viewportRef.current; if (!el || !vp) return;
    const rect = el.getBoundingClientRect?.();
    if (!rect) return;
    const cw = contentRef.current?.w || 1, ch = contentRef.current?.h || 1;
    const ms = Math.min(180 / cw, 130 / ch);
    if (!Number.isFinite(ms) || ms <= 0) return;
    const mx = (e.clientX - rect.left) / ms, my = (e.clientY - rect.top) / ms;
    const scale = tRef.current?.scale ?? 1;
    const tx = vp.clientWidth / 2 - mx * scale, ty = vp.clientHeight / 2 - my * scale;
    if (animate) animateTo({ scale, x: tx, y: ty }, 380);
    else { cancelAnim(); setT((prev) => ({ ...prev, ...clampT(tx, ty, prev?.scale ?? 1) })); }
  };

  const centerOn = (id) => {
    const p = dispRef.current?.[id]; const vp = viewportRef.current; if (!p || !vp) return;
    const scale = tRef.current?.scale ?? 1;
    animateTo({ scale, x: vp.clientWidth / 2 - (p.x + CW / 2) * scale, y: vp.clientHeight / 2 - (p.y + CH / 2) * scale }, 420);
    onSelect(id); setSearch(""); setSearchOpen(false);
  };

  // Glide-and-zoom onto a person without opening their profile (used by the tour / find-me).
  const glideTo = useCallback((id, zoom = 0.8, ms = 900) => {
    const p = dispRef.current?.[id]; const vp = viewportRef.current; if (!p || !vp) return;
    const scale = clamp(zoom, MIN_SCALE, MAX_SCALE);
    animateTo({ scale, x: vp.clientWidth / 2 - (p.x + CW / 2) * scale, y: vp.clientHeight / 2 - (p.y + CH / 2) * scale }, ms);
  }, [animateTo, CW, CH]);

  // Path from the oldest known ancestor of "me" down to me (reversed ancestor chain).
  // Falls back to the biggest branch from the first lineage root when no home is set.
  const tourPath = useMemo(() => {
    const start = homeId && byId[homeId] ? homeId : null;
    if (start) {
      const chain = [start]; let cur = start, guard = 0;
      while (guard++ < 300) {
        const parent = (byId[cur]?.parent_ids || []).find((p) => byId[p]);
        if (!parent || chain.includes(parent)) break;
        chain.push(parent); cur = parent;
      }
      return chain.reverse();
    }
    const rootId = uniqueRoots[0]; if (!rootId) return [];
    const kidsMap = {};
    members.forEach((m) => (m.parent_ids || []).forEach((p) => { (kidsMap[p] = kidsMap[p] || []).push(m.id); }));
    const chain = [rootId]; let cur = rootId, guard = 0;
    while (guard++ < 300) {
      const kids = (kidsMap[cur] || []).slice();
      if (!kids.length) break;
      kids.sort((a, b) => descendantSet(members, b).size - descendantSet(members, a).size);
      if (chain.includes(kids[0])) break;
      chain.push(kids[0]); cur = kids[0];
    }
    return chain;
  }, [homeId, byId, members, uniqueRoots]);

  // Short narration line for a tour stop, e.g. "Henry Harrington. Your great-grandfather."
  const tourLine = useCallback((id) => {
    const m = byId[id]; if (!m) return "";
    if (homeId && id === homeId) return `${m.full_name}. This is you.`;
    const rel = homeId && byId[homeId] ? kinshipLabel(members, homeId, id) : null;
    return rel ? `${m.full_name}. Your ${rel}.` : m.full_name;
  }, [byId, homeId, members]);

  const narrateStop = useCallback(async (id) => {
    stopAudio();
    if (!tourVoice) return;
    const line = tourLine(id); if (!line) return;
    try {
      const { path } = await api.tts(line, narrationVoice || "fable");
      if (!tourRef.current) return; // tour ended while audio was fetching
      const a = new Audio(audioUrl(path)); audioRef.current = a; a.play().catch(() => {});
    } catch { /* narration is best-effort */ }
  }, [tourVoice, tourLine, narrationVoice, stopAudio]);

  const startTour = () => {
    if (tourPath.length < 2) { toast.message("Add a few more generations to take a tour"); return; }
    if (!presenting) setLocked(false);
    setTourPaused(false); setTour({ i: 0 });
  };
  const tourPrev = () => setTour((tr) => (tr ? { i: Math.max(0, tr.i - 1) } : tr));
  const tourNext = () => setTour((tr) => (tr && tr.i < tourPath.length - 1 ? { i: tr.i + 1 } : tr));
  const jumpToMe = () => {
    if (!homeId || !byId[homeId]) { toast.message("Open your card and tap “This is me” first"); return; }
    stopTour(); setShowHint(false);
    if (!dispRef.current[homeId]) { fit(); return; }
    glideTo(homeId, Math.max(tRef.current?.scale ?? 1, 0.7), 650);
    setTourFocusId(homeId);
    if (tourTimer.current) clearTimeout(tourTimer.current);
    tourTimer.current = setTimeout(() => setTourFocusId(null), 2600);
  };

  // Tour driver: glide to each person in turn, narrate, and (unless paused) auto-advance.
  useEffect(() => {
    if (!tour) return;
    const id = tourPath[tour.i];
    if (id == null) { toast.success("That’s your family story 💛"); stopTour(); return; }
    setTourFocusId(id);
    glideTo(id, 0.82, 1000);
    narrateStop(id);
    if (tourPaused) return;
    tourTimer.current = setTimeout(() => setTour((tr) => (tr ? { i: tr.i + 1 } : null)), 3400);
    return () => { if (tourTimer.current) clearTimeout(tourTimer.current); };
    // eslint-disable-next-line
  }, [tour, tourPaused]);
  useEffect(() => () => { if (tourTimer.current) clearTimeout(tourTimer.current); stopAudio(); }, [stopAudio]);

  // ---------- presenter (full-screen) ----------
  const enterPresent = async () => {
    stopTour();
    try { await (viewportRef.current?.requestFullscreen?.() || Promise.resolve()); } catch { /* fullscreen optional */ }
    setPresenting(true); setLocked(true); setShowMinimap(false); setShowHint(false);
    fit();
  };
  const exitPresent = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* noop */ }
    setPresenting(false); setLocked(false); stopTour();
  };
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) { setPresenting(false); setLocked(false); stopTour(); } };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [stopTour]);

  // ---------- background / layout ----------
  const onBgFile = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { await api.uploadBackground(f); toast.success("Background updated"); onBgChanged?.(); } catch { toast.error("Could not upload background"); } e.target.value = ""; };
  const clearBg = async () => { try { await api.clearBackground(); toast.success("Background cleared"); onBgChanged?.(); } catch { toast.error("Could not clear background"); } };
  const resetLayout = async () => { try { await api.resetLayout(); toast.success("Tree auto-arranged"); onChanged?.(); } catch { toast.error("Could not auto-arrange"); } };

  // ---------- context-menu actions ----------
  const toggleCollapse = (id) => setCollapsed((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const deleteNode = async (m) => { if (!window.confirm(`Remove ${m.full_name}? Their relationships will be unlinked.`)) return; try { await api.remove(m.id); toast.success(`${m.full_name} removed`); onChanged?.(); } catch { toast.error("Could not delete"); } };
  const adopt = async (m) => { try { const r = await api.adoptSpouseChildren(m.id); toast.success(r.adopted ? `Adopted ${r.adopted} ${r.adopted === 1 ? "child" : "children"}` : "No children to adopt"); onChanged?.(); } catch { toast.error("Could not adopt children"); } };
  const combineUnion = async (m) => { try { const r = await api.combineUnionChildren(m.id); toast.success(r.combined ? `Linked ${r.combined} ${r.combined === 1 ? "child" : "children"} to the marriage` : "Children are already shared"); onChanged?.(); } catch { toast.error("Could not combine children"); } };
  const changeColor = async (m, color) => { try { await api.setLineageColor(roots[m.id], color); onSettingsChanged?.(); toast.success(color ? "Family colour updated" : "Colour reset to auto"); } catch { toast.error("Could not set colour"); } };
  const setMemberColor = async (ids, color) => { if (!canEdit || !ids.length) return; try { await Promise.all(ids.map((x) => api.update(x, { color }))); onChanged?.(); toast.success(color ? "Colour updated" : "Colour reset"); } catch { toast.error("Could not set colour"); } };
  const colorBranch = (m, color) => setMemberColor([m.id, ...descendantSet(members, m.id)], color);
  const selectBranch = (m) => { setMultiSel(new Set([m.id, ...descendantSet(members, m.id)])); toast.success("Selected this person + descendants"); };
  const openQuick = (anchor, relation = null, existing = null) => { if (!canEdit) return; setQuickConnect({ anchor, relation, existing }); };
  const mergeMembers = async (keepId, otherId) => {
    if (!canEdit || !keepId || !otherId || keepId === otherId) return;
    try { await api.merge(keepId, otherId); toast.success("Merged into one person"); setQuickConnect(null); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not merge"); }
  };
  const runUndo = async (ops) => {
    try {
      for (const op of ops) {
        if (op.type === "delete") await api.remove(op.id);
        else if (op.type === "unlink") await api.unlink(op.id, { target_id: op.target_id, relation: op.relation, link_couple: op.link_couple });
      }
      toast.success("Connection undone"); onChanged?.();
    } catch { toast.error("Could not undo"); onChanged?.(); }
  };
  const applyQuickConnect = async (payload) => {
    const qc = quickConnect; if (!qc) return;
    const anchor = byId[qc.anchor.id] || qc.anchor;
    const { relation, targetId, newName, gender, marryExisting, shareChildren, linkCouple } = payload;
    const ops = [];
    try {
      let subjectId = targetId, created = null;
      if (subjectId) {
        await api.link(subjectId, { target_id: anchor.id, relation, ...(relation === "child" ? { link_couple: linkCouple } : {}) });
        ops.push({ type: "unlink", id: subjectId, target_id: anchor.id, relation, link_couple: relation === "child" ? linkCouple : undefined });
      } else {
        created = await api.create({ full_name: newName, ...(gender ? { gender } : {}), relate_to: anchor.id, relation, ...(relation === "child" ? { link_couple: linkCouple } : {}) });
        subjectId = created.id;
        ops.push({ type: "delete", id: subjectId }); // deleting the new person unlinks everything we added
      }
      if (relation === "parent" && marryExisting) {
        const others = (anchor.parent_ids || []).filter((pid) => pid && pid !== subjectId && byId[pid]);
        await Promise.all(others.map((pid) => api.link(subjectId, { target_id: pid, relation: "spouse" })));
        if (!created) others.forEach((pid) => ops.push({ type: "unlink", id: subjectId, target_id: pid, relation: "spouse" }));
      }
      if (relation === "spouse" && shareChildren) {
        const kidsBefore = members.filter((m) => (m.parent_ids || []).includes(anchor.id) && !(m.parent_ids || []).includes(subjectId));
        await api.combineUnionChildren(anchor.id);
        if (!created) kidsBefore.forEach((k) => ops.push({ type: "unlink", id: subjectId, target_id: k.id, relation: "parent" }));
      }
      // Place a NEW parent directly above the child (beside an existing parent) when the child is manually positioned.
      if (relation === "parent" && created && typeof anchor.pos_x === "number" && typeof anchor.pos_y === "number") {
        const CG = dims.coupleGap ?? 64, RH = dims.rowH ?? 250;
        const posParent = (anchor.parent_ids || []).map((pid) => byId[pid]).find((p) => p && p.id !== subjectId && typeof p.pos_x === "number");
        const pos = posParent
          ? { pos_x: Math.round(posParent.pos_x + CW + CG), pos_y: Math.round(posParent.pos_y) }
          : { pos_x: Math.round(anchor.pos_x), pos_y: Math.round(anchor.pos_y - RH) };
        await api.update(subjectId, pos);
      }
      const subjName = created ? newName : (byId[targetId]?.full_name || "Person");
      toast.success(`${subjName} connected to ${anchor.full_name}`, { action: { label: <span data-testid="toast-undo-button">Undo</span>, onClick: () => runUndo(ops) } });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect"); onChanged?.(); throw e; }
  };
  const assignSelectionToGroup = async (gid) => { const ids = [...multiSel]; if (!ids.length) return; try { await Promise.all(ids.map((id) => api.groupSetMember(gid, id, "add"))); toast.success(`Added ${ids.length} to group`); onGroupsChanged?.(); } catch { toast.error("Could not add to group"); } };
  const groupFromSelection = async () => {
    const ids = [...multiSel]; if (!ids.length) return;
    const name = window.prompt("Name this group (e.g. \"Mum's side\")"); if (!name) return;
    try { const g = await api.createGroup(name.trim()); await Promise.all(ids.map((id) => api.groupSetMember(g.id, id, "add"))); toast.success(`Group "${g.name}" created with ${ids.length}`); onGroupsChanged?.(); setShowGroups(true); } catch { toast.error("Could not create group"); }
  };
  const themeBranchMap = (rootId, theme) => {
    const cols = theme?.colors || [];
    if (!cols.length) return {};
    const childrenMap = {};
    members.forEach((m) => (m.parent_ids || []).forEach((p) => { (childrenMap[p] = childrenMap[p] || []).push(m.id); }));
    const map = { [rootId]: cols[0] };
    (childrenMap[rootId] || []).forEach((kid, i) => { const col = cols[(i + 1) % cols.length]; map[kid] = col; descendantSet(members, kid).forEach((d) => { map[d] = col; }); });
    return map;
  };
  const applyThemeToBranch = async (rootId, theme) => {
    if (!canEdit || !theme?.colors?.length) return;
    setThemePreview(null);
    const updates = themeBranchMap(rootId, theme);
    try { await Promise.all(Object.entries(updates).map(([id, c]) => api.update(id, { color: c }))); onChanged?.(); toast.success(`Applied "${theme.name}" to this branch`); } catch { toast.error("Could not apply theme"); }
  };
  const saveCurrentPalette = async () => {
    const cols = [...new Set(vis.map((m) => (colorFor(m.id) || "").toLowerCase()).filter(Boolean))].slice(0, 10);
    if (!cols.length) { toast.info("No colours in the tree yet"); return; }
    const name = window.prompt("Name this colour theme", familyName ? `${familyName} palette` : "My theme");
    if (!name) return;
    try { await api.createTheme(name.trim(), cols); toast.success("Theme saved"); reloadThemes(); } catch { toast.error("Could not save theme"); }
  };
  const deleteThemeFn = async (t) => { if (!window.confirm(`Delete theme "${t.name}"?`)) return; try { await api.deleteTheme(t.id); toast.success("Theme deleted"); reloadThemes(); } catch { toast.error("Could not delete"); } };
  const applyGroupColor = async (g) => {
    const ids = g.member_ids || [];
    if (!ids.length) { toast.info("No members in this group yet"); return; }
    try { await Promise.all(ids.map((id) => api.update(id, { color: g.color }))); onChanged?.(); toast.success(`Applied ${g.name} colour to ${ids.length}`); } catch { toast.error("Could not apply colour"); }
  };

  // ---------- named groups ----------
  const addToGroup = async (gid, memberId) => { try { await api.groupSetMember(gid, memberId, "add"); toast.success("Added to group"); onGroupsChanged?.(); } catch { toast.error("Could not add to group"); } };
  const removeFromGroup = async (gid, memberId) => { try { await api.groupSetMember(gid, memberId, "remove"); onGroupsChanged?.(); } catch { toast.error("Could not update group"); } };
  const createGroupWith = async (memberId) => { const name = window.prompt('Name this group (e.g. "Mum\'s side")'); if (!name) return; try { const g = await api.createGroup(name); if (memberId) await api.groupSetMember(g.id, memberId, "add"); toast.success(`Group "${g.name}" created`); onGroupsChanged?.(); setShowGroups(true); } catch { toast.error("Could not create group"); } };
  const renameGroup = async (g) => { const name = window.prompt("Rename group", g.name); if (name == null) return; try { await api.updateGroup(g.id, { name: name.trim() || g.name }); onGroupsChanged?.(); } catch { toast.error("Could not rename"); } };
  const recolorGroup = async (g, color) => { try { await api.updateGroup(g.id, { color }); onGroupsChanged?.(); } catch { toast.error("Could not recolour"); } };
  const deleteGroupFn = async (g) => { if (!window.confirm(`Delete group "${g.name}"? (people are kept)`)) return; try { await api.deleteGroup(g.id); setFocusGroups((s) => { const n = new Set(s); n.delete(g.id); return n; }); onGroupsChanged?.(); } catch { toast.error("Could not delete group"); } };
  const toggleFocusGroup = (gid) => setFocusGroups((s) => { const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });

  // ---------- export ----------
  const captureInner = async () => {
    const el = innerRef.current;
    return html2canvas(el, { backgroundColor: "#F4F6F8", scale: 2, useCORS: true, width: Math.ceil(contentRef.current.w), height: Math.ceil(contentRef.current.h) });
  };
  const saveCanvas = (canvas, asPdf, name) => {
    if (asPdf) {
      const img = canvas.toDataURL("image/png");
      const w = canvas.width / 2, h = canvas.height / 2;
      const pdf = new jsPDF({ orientation: w > h ? "landscape" : "portrait", unit: "pt", format: [w, h] });
      pdf.addImage(img, "PNG", 0, 0, w, h);
      pdf.save(`${name}.pdf`);
    } else {
      const a = document.createElement("a"); a.href = canvas.toDataURL("image/png"); a.download = `${name}.png`; a.click();
    }
  };
  const exportTree = async (asPdf, withLegend = false, meta = null) => {
    if (!innerRef.current) return;
    setExporting(true);
    await new Promise((r) => setTimeout(r, 80));
    try {
      let canvas = await captureInner();
      if (withLegend) canvas = composeLegend(canvas, meta);
      saveCanvas(canvas, asPdf, withLegend ? "family-tree-legend" : "family-tree");
      toast.success(asPdf ? "PDF downloaded" : "Image downloaded");
    } catch (e) { toast.error("Could not export the tree"); } finally { setExporting(false); }
  };
  // Print-ready: tile the tree across A4/Letter pages with corner cut marks.
  const exportPrintTiled = async (withCover = false) => {
    if (!innerRef.current) return;
    setExporting(true);
    await new Promise((r) => setTimeout(r, 80));
    try {
      const full = await captureInner();
      const S = 2; // captureInner scale
      const pageW = 842, pageH = 595, margin = 32, mark = 14; // A4 landscape (pt)
      const usableW = pageW - margin * 2, usableH = pageH - margin * 2;
      const printW = full.width / S, printH = full.height / S; // pt at 1 CSS px = 1 pt
      const cols = Math.max(1, Math.ceil(printW / usableW));
      const rows = Math.max(1, Math.ceil(printH / usableH));
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      let first = true;
      if (withCover) {
        // Optional keepsake cover page: emblem + family name + date.
        const fam = (familyName || "Our").replace(/^The\s+/i, "");
        const logo = await new Promise((res) => { const img = new Image(); img.onload = () => { const c = document.createElement("canvas"); c.width = img.naturalWidth; c.height = img.naturalHeight; c.getContext("2d").drawImage(img, 0, 0); res({ url: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight }); }; img.onerror = () => res(null); img.src = "/logo-mark.png"; });
        pdf.setFillColor(244, 246, 248); pdf.rect(0, 0, pageW, pageH, "F");
        pdf.setDrawColor(212, 160, 23); pdf.setLineWidth(3); pdf.rect(28, 28, pageW - 56, pageH - 56);
        if (logo) { const lw = 96, lh = lw * (logo.h / logo.w); pdf.addImage(logo.url, "PNG", (pageW - lw) / 2, 150, lw, lh); }
        pdf.setTextColor(8, 45, 74); pdf.setFont("times", "bold"); pdf.setFontSize(40);
        pdf.text(`The ${fam} Family`, pageW / 2, 320, { align: "center" });
        pdf.setDrawColor(212, 160, 23); pdf.setLineWidth(2); pdf.line(pageW / 2 - 70, 342, pageW / 2 + 70, 342);
        pdf.setTextColor(90, 107, 122); pdf.setFont("times", "normal"); pdf.setFontSize(18);
        pdf.text(legendDate || new Date().toLocaleDateString(), pageW / 2, 372, { align: "center" });
        pdf.setTextColor(150, 150, 150); pdf.setFontSize(11);
        pdf.text("whereamifrom.net", pageW / 2, pageH - 46, { align: "center" });
        first = false;
      }
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (!first) pdf.addPage("a4", "landscape");
          first = false;
          const sxPt = c * usableW, syPt = r * usableH;
          const swPt = Math.min(usableW, printW - sxPt), shPt = Math.min(usableH, printH - syPt);
          const sx = sxPt * S, sy = syPt * S, sw = swPt * S, sh = shPt * S;
          const tile = document.createElement("canvas"); tile.width = Math.max(1, sw); tile.height = Math.max(1, sh);
          const tctx = tile.getContext("2d"); tctx.fillStyle = "#FFFFFF"; tctx.fillRect(0, 0, tile.width, tile.height);
          tctx.drawImage(full, sx, sy, sw, sh, 0, 0, sw, sh);
          pdf.addImage(tile.toDataURL("image/png"), "PNG", margin, margin, swPt, shPt);
          // corner cut marks
          pdf.setDrawColor(120); pdf.setLineWidth(0.6);
          const x0 = margin, y0 = margin, x1 = margin + swPt, y1 = margin + shPt;
          [[x0, y0, 1, 1], [x1, y0, -1, 1], [x0, y1, 1, -1], [x1, y1, -1, -1]].forEach(([px, py, dx, dy]) => {
            pdf.line(px, py, px + dx * mark, py); pdf.line(px, py, px, py + dy * mark);
          });
          pdf.setFontSize(8); pdf.setTextColor(150);
          pdf.text(`Page ${r * cols + c + 1} of ${rows * cols}  ·  row ${r + 1}, col ${c + 1}`, margin, pageH - 12);
        }
      }
      pdf.save("family-tree-print.pdf");
      toast.success(`Print-ready PDF (${rows * cols}${withCover ? " + cover" : ""} pages)`);
    } catch (e) { toast.error("Could not build print PDF"); } finally { setExporting(false); }
  };
  // Export just the currently multi-selected people (cropped to their bounding box).
  const exportSelection = async () => {
    const ids = [...multiSel];
    if (!ids.length || !innerRef.current) return;
    setExporting(true);
    await new Promise((r) => setTimeout(r, 120));
    try {
      const full = await captureInner();
      const S = 2, pad = 44;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      ids.forEach((id) => { const p = disp(id); if (!p) return; minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x + CW); maxY = Math.max(maxY, p.y + CH); });
      if (!isFinite(minX)) throw new Error("no visible nodes");
      const sx = Math.max(0, (minX - pad) * S), sy = Math.max(0, (minY - pad) * S);
      const sw = Math.min(full.width - sx, (maxX - minX + pad * 2) * S), sh = Math.min(full.height - sy, (maxY - minY + pad * 2) * S);
      const out = document.createElement("canvas"); out.width = Math.max(1, sw); out.height = Math.max(1, sh);
      const ctx = out.getContext("2d"); ctx.fillStyle = "#F4F6F8"; ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(full, sx, sy, sw, sh, 0, 0, sw, sh);
      saveCanvas(out, false, "family-selection");
      toast.success(`Exported ${ids.length} selected`);
    } catch (e) { toast.error("Could not export selection"); } finally { setExporting(false); }
  };
  // Auto colour legend: distinct effective colours → person / branch label.
  const buildColorLegend = () => {
    const byColor = {};
    vis.forEach((m) => { const c = (colorFor(m.id) || "").toLowerCase(); (byColor[c] = byColor[c] || []).push(m.id); });
    const shortNameOf = (id) => byId[id]?.nickname || byId[id]?.first_name || (byId[id]?.full_name || "").split(" ")[0] || byId[id]?.full_name || "Someone";
    return Object.entries(byColor).map(([color, ids]) => {
      const idSet = new Set(ids);
      const custom = ids.some((id) => (byId[id]?.color || "").toLowerCase() === color);
      let label;
      if (custom) {
        const tops = ids.filter((id) => !((byId[id]?.parent_ids || []).some((p) => idSet.has(p))));
        const names = tops.map(shortNameOf);
        if (tops.length === 1 && ids.length > 1) label = `${names[0]}'s branch`;
        else label = names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3} more` : "");
      } else {
        const rootId = roots[ids[0]];
        label = byId[rootId]?.last_name || byId[rootId]?.full_name || "Family";
      }
      return { color, label };
    });
  };
  const composeLegend = (tree, meta = null) => {
    const legend = buildColorLegend();
    if (!legend.length) return tree;
    const title = (meta?.title || "").trim();
    const date = (meta?.date || "").trim();
    const note = (meta?.note || "").trim();
    const titleH = title || date || note ? (note ? 250 : 200) : 0;
    const colW = 700, rowH = 82, padX = 80, headH = 150, padBottom = 60;
    const cols = Math.max(1, Math.min(legend.length, Math.floor(tree.width / colW) || 1));
    const rows = Math.ceil(legend.length / cols);
    const footH = titleH + headH + rows * rowH + padBottom;
    const out = document.createElement("canvas");
    out.width = Math.max(tree.width, cols * colW + padX * 2);
    out.height = tree.height + footH;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#F4F6F8"; ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(tree, (out.width - tree.width) / 2, 0);
    let fy = tree.height;
    if (titleH) {
      ctx.textAlign = "center";
      if (title) { ctx.fillStyle = "#082D4A"; ctx.font = "700 68px Georgia, 'Times New Roman', serif"; ctx.fillText(title, out.width / 2, fy + 96); }
      ctx.strokeStyle = "#D4A017"; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(out.width / 2 - 90, fy + 128); ctx.lineTo(out.width / 2 + 90, fy + 128); ctx.stroke();
      if (date) { ctx.fillStyle = "#5A6B7A"; ctx.font = "400 34px Georgia, serif"; ctx.fillText(date, out.width / 2, fy + 172); }
      if (note) { ctx.fillStyle = "#475569"; ctx.font = "italic 400 30px Georgia, serif"; ctx.fillText(note.length > 90 ? note.slice(0, 88) + "…" : note, out.width / 2, fy + 218); }
      fy += titleH;
    }
    ctx.textAlign = "left"; ctx.strokeStyle = "#D8E0E8"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(padX, fy + 44); ctx.lineTo(out.width - padX, fy + 44); ctx.stroke();
    ctx.fillStyle = "#082D4A"; ctx.font = "700 46px Georgia, 'Times New Roman', serif";
    ctx.fillText("Colour legend", padX, fy + 116);
    legend.forEach((e, i) => {
      const col = i % cols, row = Math.floor(i / cols);
      const x = padX + col * colW, y = fy + headH + row * rowH;
      ctx.fillStyle = e.color; ctx.fillRect(x, y, 46, 46);
      ctx.strokeStyle = "rgba(0,0,0,0.12)"; ctx.lineWidth = 2; ctx.strokeRect(x, y, 46, 46);
      ctx.fillStyle = "#334155"; ctx.font = "400 34px Georgia, serif"; ctx.textAlign = "left";
      let label = e.label; const maxW = colW - 100;
      while (label.length > 4 && ctx.measureText(label).width > maxW) label = label.slice(0, -2);
      if (label !== e.label) label = label.replace(/\.*$/, "") + "…";
      ctx.fillText(label, x + 66, y + 33);
    });
    return out;
  };
  const openLegendDialog = (asPdf) => { setLegendTitle((prev) => prev || (familyName ? `The ${familyName.replace(/^The\s+/i, "")}` : "")); setLegendDialog({ asPdf }); };
  const runLegendExport = () => {
    const meta = { title: legendTitle, date: legendDate, note: legendNote };
    localStorage.setItem("familyroots_legend_title", legendTitle);
    localStorage.setItem("familyroots_legend_note", legendNote);
    const asPdf = legendDialog?.asPdf;
    setLegendDialog(null);
    exportTree(asPdf, true, meta);
  };
  // Fan chart keepsake poster — ensures radial view, adds a titled header band.
  const exportFanChart = async (asPdf) => {
    if (!innerRef.current) return;
    const wasRadial = orientation === "radial";
    if (!wasRadial) { setOrientation("radial"); await new Promise((r) => setTimeout(r, 450)); }
    setExporting(true);
    await new Promise((r) => setTimeout(r, 120));
    try {
      const tree = await captureInner();
      const focus = byId[radialFocus] || byId[selectedId] || members[0];
      const family = focus?.last_name || focus?.full_name || "Our";
      const title = `The ${family} Family`;
      const subtitle = `Fan Chart · ${new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`;
      const headH = 220; // device px (scale 2)
      const out = document.createElement("canvas");
      out.width = Math.max(tree.width, 900);
      out.height = tree.height + headH;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#F4F6F8"; ctx.fillRect(0, 0, out.width, out.height);
      ctx.strokeStyle = "#D4A017"; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(out.width / 2 - 90, headH - 44); ctx.lineTo(out.width / 2 + 90, headH - 44); ctx.stroke();
      ctx.textAlign = "center";
      ctx.fillStyle = "#082D4A"; ctx.font = "700 72px Georgia, 'Times New Roman', serif";
      ctx.fillText(title, out.width / 2, 108);
      ctx.fillStyle = "#5A6B7A"; ctx.font = "400 34px Georgia, serif";
      ctx.fillText(subtitle, out.width / 2, 160);
      ctx.drawImage(tree, (out.width - tree.width) / 2, headH);
      saveCanvas(out, asPdf, "family-fan-chart");
      toast.success("Fan chart poster downloaded");
    } catch (e) { toast.error("Could not export the fan chart"); } finally { setExporting(false); }
  };

  // ---------- highlight / focus sets ----------
  const adj = buildAdjacency(members);
  const pathIds = pathSel.length === 2 ? shortestPath(adj, pathSel[0], pathSel[1]) : [];
  const pathSet = new Set(pathIds);
  let focusUnion = null;
  if (focusGroups.size) { focusUnion = new Set(); groups.forEach((g) => { if (focusGroups.has(g.id)) (g.member_ids || []).forEach((id) => focusUnion.add(id)); }); }
  const highlightSet = pathSet.size ? pathSet : focusUnion; // Set | null
  const dimConn = highlightSet ? 0.12 : 1;

  // plain-English kinship label for the two selected relatives
  const pathKinship = pathIds.length >= 2 ? kinshipLabel(members, pathSel[0], pathSel[1]) : null;

  // focused groups (id, name, colour) + per-member spotlight colour
  const focusedGroups = groups.filter((g) => focusGroups.has(g.id));
  const focusColorFor = (id) => {
    for (const g of focusedGroups) { if ((g.member_ids || []).includes(id)) return g.color || "#082D4A"; }
    return null;
  };

  // ---------- connectors ----------
  const spouseLines = []; const hearts = []; const seen = new Set();
  if (!isRadial) vis.forEach((m) => {
    const a = disp(m.id); if (!a) return;
    (m.spouse_ids || []).forEach((sid) => {
      if (!layout.nodes[sid]) return;
      const key = [m.id, sid].sort().join("-"); if (seen.has(key)) return; seen.add(key);
      const b = disp(sid); if (!b) return;
      const acx = a.x + CW / 2, acy = a.y + CH / 2, bcx = b.x + CW / 2, bcy = b.y + CH / 2;
      let x1, y1, x2, y2;
      if (Math.abs(bcx - acx) >= Math.abs(bcy - acy)) {
        const L = a.x <= b.x ? a : b, R = a.x <= b.x ? b : a;
        x1 = L.x + CW; y1 = L.y + CH / 2; x2 = R.x; y2 = R.y + CH / 2;
      } else {
        const Tp = a.y <= b.y ? a : b, Bt = a.y <= b.y ? b : a;
        x1 = Tp.x + CW / 2; y1 = Tp.y + CH; x2 = Bt.x + CW / 2; y2 = Bt.y;
      }
      spouseLines.push({ key, x1, y1, x2, y2 });
      hearts.push({ key, x: (x1 + x2) / 2, y: (y1 + y2) / 2 });
    });
  });
  const childPaths = [];
  const parentExit = (d) => {
    if (orientation === "down") return { x: d.x + CW / 2, y: d.y + CH };
    if (orientation === "up") return { x: d.x + CW / 2, y: d.y };
    if (orientation === "right") return { x: d.x + CW, y: d.y + CH / 2 };
    return { x: d.x, y: d.y + CH / 2 };
  };
  const childEntry = (d) => {
    if (orientation === "down") return { x: d.x + CW / 2, y: d.y };
    if (orientation === "up") return { x: d.x + CW / 2, y: d.y + CH };
    if (orientation === "right") return { x: d.x, y: d.y + CH / 2 };
    return { x: d.x + CW, y: d.y + CH / 2 };
  };
  vis.forEach((m) => {
    const c = disp(m.id); if (!c) return;
    const ps = (m.parent_ids || []).map(disp).filter(Boolean);
    if (!ps.length) return;
    if (isRadial) {
      const cc = { x: c.x + CW / 2, y: c.y + CH / 2 };
      ps.forEach((p, i) => { const pc = { x: p.x + CW / 2, y: p.y + CH / 2 }; childPaths.push({ key: `${m.id}-${i}`, d: `M ${cc.x} ${cc.y} L ${pc.x} ${pc.y}` }); });
      return;
    }
    const exits = ps.map(parentExit);
    const ex = exits.reduce((s, p) => s + p.x, 0) / exits.length;
    const ey = exits.reduce((s, p) => s + p.y, 0) / exits.length;
    const en = childEntry(c);
    let d;
    if (isVertical) { const mid = (ey + en.y) / 2; d = `M ${ex} ${ey} C ${ex} ${mid}, ${en.x} ${mid}, ${en.x} ${en.y}`; }
    else { const mid = (ex + en.x) / 2; d = `M ${ex} ${ey} C ${mid} ${ey}, ${mid} ${en.y}, ${en.x} ${en.y}`; }
    childPaths.push({ key: m.id, d });
  });

  // path highlight overlay (straight segments between consecutive path members)
  const pathSegments = [];
  for (let i = 0; i < pathIds.length - 1; i++) {
    const a = disp(pathIds[i]), b = disp(pathIds[i + 1]);
    if (a && b) pathSegments.push({ key: `${pathIds[i]}-${pathIds[i + 1]}`, x1: a.x + CW / 2, y1: a.y + CH / 2, x2: b.x + CW / 2, y2: b.y + CH / 2 });
  }

  // ---------- bands ----------
  const bboxOf = (ids) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ids.forEach((id) => { const p = disp(id); if (!p) return; minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x + CW); maxY = Math.max(maxY, p.y + CH); });
    return isFinite(minX) ? { minX, minY, maxX, maxY } : null;
  };
  const bands = [];
  if (showBands && uniqueRoots.length) {
    const gmap = {}; vis.forEach((m) => { (gmap[roots[m.id]] = gmap[roots[m.id]] || []).push(m.id); });
    Object.entries(gmap).forEach(([root, ids]) => {
      if (ids.length < 2) return; const bb = bboxOf(ids); if (!bb) return;
      const pad = 26, top = 26;
      bands.push({ key: root, x: bb.minX - pad, y: bb.minY - pad - top, w: bb.maxX - bb.minX + pad * 2, h: bb.maxY - bb.minY + pad * 2 + top, color: colorFor(root), label: byId[root]?.last_name || byId[root]?.full_name || "Family" });
    });
  }
  const groupBands = [];
  if (showGroups) groups.forEach((g) => {
    const ids = (g.member_ids || []).filter((id) => layout.nodes[id]); if (!ids.length) return;
    const bb = bboxOf(ids); if (!bb) return; const pad = 18, top = 26;
    groupBands.push({ key: g.id, x: bb.minX - pad, y: bb.minY - pad - top, w: bb.maxX - bb.minX + pad * 2, h: bb.maxY - bb.minY + pad * 2 + top, color: g.color || "#082D4A", label: g.name });
  });

  const searchMatches = search.trim().length >= 1
    ? members.filter((m) => (m.full_name || "").toLowerCase().includes(search.trim().toLowerCase())).slice(0, 6)
    : [];

  const bgStyle = bgUrl ? { backgroundImage: `linear-gradient(rgba(250,247,242,0.72), rgba(250,247,242,0.72)), url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined;
  const innerStyle = exporting
    ? { transform: "none", width: Math.ceil(contentRef.current.w), height: Math.ceil(contentRef.current.h), backgroundColor: "#F4F6F8" }
    : { transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})` };
  // "Lost" = almost none of the tree is on screen → gently highlight Back to tree.
  const lost = overlapFrac(t.x, t.y, t.scale) < 0.06;

  return (
    <div
      ref={viewportRef}
      data-testid="tree-canvas"
      className={`relative h-full w-full overflow-hidden no-select ${bgUrl ? "" : "tree-canvas-bg"}`}
      style={{ cursor: frameMode ? "crosshair" : (panRef.current?.active ? "grabbing" : "grab"), touchAction: "none", ...bgStyle }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onContextMenu={(e) => { if (suppressCtxRef.current) { e.preventDefault(); suppressCtxRef.current = false; } }}
    >
      {showHint && (
        <div data-testid="first-time-hint" className="absolute left-1/2 top-4 z-50 flex max-w-[92%] -translate-x-1/2 items-start gap-2.5 rounded-2xl border border-[#D4A017]/40 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
          <Info size={16} className="mt-0.5 shrink-0 text-[#D4A017]" />
          <p className="text-xs text-[#475569]">You're <span className="font-semibold text-[#082D4A]">browsing</span> — drag to look around and tap anyone to read their story. Press <span className="font-semibold text-[#082D4A]">Edit tree</span> to make changes.</p>
          <button onClick={dismissHint} data-testid="dismiss-hint-button" className="ml-1 shrink-0 rounded-full p-0.5 text-[#94A3B8] hover:bg-[#E8EEF3] hover:text-[#082D4A]"><X size={15} /></button>
        </div>
      )}
      {multiSel.size > 0 && (
        <MultiSelectBar count={multiSel.size} canEdit={canEdit} groups={groups} palette={PALETTE}
          onColor={(c) => setMemberColor([...multiSel], c)} onMoreColors={() => setColorPicker({ ids: [...multiSel], title: `${multiSel.size} selected` })}
          onResetColor={() => setMemberColor([...multiSel], null)} onAssignGroup={assignSelectionToGroup} onMakeGroup={groupFromSelection}
          onFrame={frameSelection} onExport={exportSelection} onClear={() => setMultiSel(new Set())} />
      )}
      <div ref={innerRef} className="absolute left-0 top-0 origin-top-left" style={innerStyle}>
        {frames.map((f0) => {
          const f = getFrame(f0);
          const c1 = dispPoint({ x: f.x, y: f.y });
          const c2 = dispPoint({ x: f.x + f.w, y: f.y + f.h });
          const left = Math.min(c1.x, c2.x), top = Math.min(c1.y, c2.y);
          const width = Math.abs(c2.x - c1.x), height = Math.abs(c2.y - c1.y);
          return (
            <div key={f0.id} data-testid={`frame-${f0.id}`} className="pointer-events-none absolute z-[3] rounded-2xl" style={{ left, top, width, height, backgroundColor: hexA(f.color, 0.06), border: `2px solid ${hexA(f.color, 0.6)}` }}>
              <span className={`pointer-events-auto absolute -top-3 left-4 flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white shadow ${editing ? "cursor-grab active:cursor-grabbing" : ""}`} style={{ backgroundColor: f.color }} onPointerDown={editing ? (e) => onFrameBodyDown(e, f0) : undefined}>
                {f.label}
                {editing && (
                  <>
                    <button onClick={() => renameFrame(f0)} onPointerDown={(e) => e.stopPropagation()} data-testid={`frame-rename-${f0.id}`} className="ml-1 opacity-80 hover:opacity-100"><PencilIcon size={11} /></button>
                    <button onClick={() => deleteFrameFn(f0)} onPointerDown={(e) => e.stopPropagation()} data-testid={`frame-delete-${f0.id}`} className="opacity-80 hover:opacity-100"><X size={12} /></button>
                  </>
                )}
              </span>
              {editing && (
                <div className="pointer-events-auto absolute -bottom-2 right-6 flex gap-1" onPointerDown={(e) => e.stopPropagation()}>
                  {PALETTE.slice(0, 5).map((c) => (<button key={c} onClick={() => recolorFrame(f0, c)} className="h-3.5 w-3.5 rounded-full ring-1 ring-black/10 hover:scale-110" style={{ backgroundColor: c }} />))}
                </div>
              )}
              {editing && <div onPointerDown={(e) => onFrameResizeDown(e, f0)} data-testid={`frame-resize-${f0.id}`} className="pointer-events-auto absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize rounded-br-2xl" style={{ backgroundColor: hexA(f.color, 0.55) }} />}
            </div>
          );
        })}
        {drawFrame && (() => {
          const c1 = dispPoint({ x: drawFrame.x, y: drawFrame.y }), c2 = dispPoint({ x: drawFrame.x + drawFrame.w, y: drawFrame.y + drawFrame.h });
          return <div data-testid="draw-frame-preview" className="pointer-events-none absolute z-[3] rounded-2xl border-2 border-dashed border-[#082D4A] bg-[#082D4A]/5" style={{ left: Math.min(c1.x, c2.x), top: Math.min(c1.y, c2.y), width: Math.abs(c2.x - c1.x), height: Math.abs(c2.y - c1.y) }} />;
        })()}
        {marquee && <div data-testid="marquee-box" className="pointer-events-none absolute z-[25] rounded border-2 border-dashed border-[#3B82F6] bg-[#3B82F6]/10" style={{ left: Math.min(marquee.x0, marquee.x1), top: Math.min(marquee.y0, marquee.y1), width: Math.abs(marquee.x1 - marquee.x0), height: Math.abs(marquee.y1 - marquee.y0) }} />}
        {bands.map((b) => (
          <div key={b.key} data-testid={`family-band-${b.key}`} className="absolute z-[5] rounded-[28px]" style={{ left: b.x, top: b.y, width: b.w, height: b.h, backgroundColor: hexA(b.color, 0.07), border: `2px dashed ${hexA(b.color, 0.45)}` }}>
            <span className="absolute left-4 top-2 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white" style={{ backgroundColor: b.color }}>{b.label}</span>
          </div>
        ))}
        {groupBands.map((b) => (
          <div key={b.key} data-testid={`group-band-${b.key}`} className="absolute z-[4] rounded-[24px]" style={{ left: b.x, top: b.y, width: b.w, height: b.h, backgroundColor: hexA(b.color, 0.1), border: `2px solid ${hexA(b.color, 0.55)}` }}>
            <span className="absolute left-4 top-2 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white shadow" style={{ backgroundColor: b.color }}>{b.label}</span>
          </div>
        ))}

        <svg className="pointer-events-none absolute left-0 top-0 z-[6] overflow-visible" width={contentRef.current.w} height={contentRef.current.h}>
          {childPaths.map((p) => (<path key={p.key} d={p.d} fill="none" stroke="#64748B" strokeWidth={2.5} opacity={dimConn} />))}
          {spouseLines.map((l) => (<line key={l.key} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#D4A017" strokeWidth={2.5} strokeDasharray="5 4" opacity={dimConn} />))}
          {pathSegments.map((s) => (<line key={s.key} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke="#D4A017" strokeWidth={5} strokeLinecap="round" data-testid={`path-segment-${s.key}`} />))}
        </svg>

        {hearts.map((h) => (
          <div key={h.key} className="absolute z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#D4A017] shadow-sm" style={{ left: h.x, top: h.y, opacity: dimConn }}>
            <Heart size={14} fill="#D4A017" />
          </div>
        ))}

        {vis.map((m) => {
          const p = disp(m.id); if (!p) return null;
          const isDrop = dropTarget === m.id;
          const hc = hiddenCountFor(m.id);
          const isCollapsed = collapsed.has(m.id);
          const dim = highlightSet && !highlightSet.has(m.id);
          const onPath = pathSet.has(m.id);
          const fc = focusedGroups.length > 1 ? focusColorFor(m.id) : null;
          const pathIdx = pathIds.indexOf(m.id);
          const enterDelay = isVertical && !dim ? Math.min(Math.round(p.y / (dims.rowH || 300)) * 70, 560) : 0;
          const wrapper = (
            <div
              className={`group absolute z-20 ${dim ? "" : "tree-card-enter"} ${editing ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${isDrop ? "rounded-2xl ring-4 ring-[#082D4A]/50" : ""} ${onPath ? "rounded-2xl ring-4 ring-[#D4A017]" : ""} ${multiSel.has(m.id) ? "rounded-2xl ring-4 ring-[#3B82F6]" : ""} ${tourFocusId === m.id ? "rounded-2xl ring-4 ring-[#D4A017] shadow-xl" : ""}`}
              style={{ left: p.x, top: p.y, width: CW, opacity: dim ? 0.16 : 1, transition: "opacity 150ms", animationDelay: `${enterDelay}ms`, ...(fc && !onPath && !isDrop ? { boxShadow: `0 0 0 4px ${fc}`, borderRadius: 16 } : {}) }}
              onPointerDown={(e) => onNodePointerDown(e, m.id)}
              data-testid={`tree-node-${m.id}`}
            >
              {onPath && (pathIdx === 0 || pathIdx === pathIds.length - 1) && (
                <span className="absolute -left-2 -top-2 z-30 rounded-full bg-[#D4A017] px-1.5 py-0.5 text-[10px] font-bold text-white shadow">{pathIdx === 0 ? "A" : "B"}</span>
              )}
              <PersonCard member={m} onClick={() => {}} active={m.id === selectedId} accent={colorBy ? colorFor(m.id) : null} dragging={nodeDrag?.id === m.id} variant={theme} isHome={m.id === homeId} needsInfo={!m.birth_date && !m.death_date} faded={isMarriedIn(m) && m.id !== selectedId && !onPath} fadedOpacity={bloodlineOnly ? 0.2 : 0.6} />
              {isCollapsed && hc > 0 && (<span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-[#082D4A] px-2 py-0.5 text-[10px] font-semibold text-white shadow" data-testid={`collapsed-badge-${m.id}`}>+{hc}</span>)}
              {editing && (
                <>
                  <button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); openQuick(m, "parent"); }} data-testid={`quick-add-parent-${m.id}`} title="Add parent" className="absolute -top-3 left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-[#082D4A] text-white opacity-0 shadow-md transition-opacity duration-150 hover:bg-[#08223A] group-hover:opacity-100"><Plus size={14} /></button>
                  {!isCollapsed && (<button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); openQuick(m, "child"); }} data-testid={`quick-add-child-${m.id}`} title="Add child" className="absolute -bottom-3 left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-[#D4A017] text-white opacity-0 shadow-md transition-opacity duration-150 hover:bg-[#B0850F] group-hover:opacity-100"><Plus size={14} /></button>)}
                  <button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); openQuick(m, "spouse"); }} data-testid={`quick-add-spouse-${m.id}`} title="Add spouse / partner" className="absolute -right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[#D4A017] text-white opacity-0 shadow-md transition-opacity duration-150 hover:bg-[#B0850F] group-hover:opacity-100"><Heart size={13} /></button>
                  <button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); openQuick(m, "sibling"); }} data-testid={`quick-add-sibling-${m.id}`} title="Add sibling" className="absolute -left-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[#3B82F6] text-white opacity-0 shadow-md transition-opacity duration-150 hover:bg-[#2563EB] group-hover:opacity-100"><UserPlus size={13} /></button>
                </>
              )}
            </div>
          );
          if (!editing) return <React.Fragment key={m.id}>{wrapper}</React.Fragment>;
          return (
            <ContextMenu key={m.id} onOpenChange={(o) => { if (!o) setThemePreview(null); }}>
              <ContextMenuTrigger asChild>{wrapper}</ContextMenuTrigger>
              <ContextMenuContent className="w-56" data-testid={`node-context-menu-${m.id}`}>
                <ContextMenuItem onClick={() => onSelect(m.id)} data-testid={`ctx-open-${m.id}`}><Eye size={14} className="mr-2" /> Open</ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={() => openQuick(m, "parent")}><Users size={14} className="mr-2" /> Add parent</ContextMenuItem>
                <ContextMenuItem onClick={() => openQuick(m, "child")} data-testid={`ctx-add-child-${m.id}`}><Baby size={14} className="mr-2" /> Add child</ContextMenuItem>
                <ContextMenuItem onClick={() => openQuick(m, "spouse")}><Heart size={14} className="mr-2" /> Add spouse</ContextMenuItem>
                <ContextMenuItem onClick={() => openQuick(m, "sibling")}><UserPlus size={14} className="mr-2" /> Add sibling</ContextMenuItem>
                {(m.spouse_ids || []).length > 0 && (<ContextMenuItem onClick={() => adopt(m)} data-testid={`ctx-adopt-${m.id}`}><Baby size={14} className="mr-2" /> Adopt spouse's children</ContextMenuItem>)}
                {(m.spouse_ids || []).length > 0 && (<ContextMenuItem onClick={() => combineUnion(m)} data-testid={`ctx-combine-${m.id}`}><Heart size={14} className="mr-2" /> Combine children under marriage</ContextMenuItem>)}
                <ContextMenuSeparator />
                {hc > 0 && (<ContextMenuItem onClick={() => toggleCollapse(m.id)} data-testid={`ctx-collapse-${m.id}`}>{isCollapsed ? <><Eye size={14} className="mr-2" /> Expand branch</> : <><EyeOff size={14} className="mr-2" /> Collapse branch</>}</ContextMenuItem>)}
                <ContextMenuSub>
                  <ContextMenuSubTrigger data-testid={`ctx-group-${m.id}`}><UsersRound size={14} className="mr-2" /> Groups</ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-52">
                    {groups.map((g) => { const inG = (g.member_ids || []).includes(m.id); return (<ContextMenuItem key={g.id} onClick={() => (inG ? removeFromGroup(g.id, m.id) : addToGroup(g.id, m.id))} data-testid={`ctx-group-${g.id}-${m.id}`}><span className="mr-2 h-3 w-3 rounded-full" style={{ backgroundColor: g.color }} />{inG ? "✓ " : ""}{g.name}</ContextMenuItem>); })}
                    {groups.length > 0 && <ContextMenuSeparator />}
                    <ContextMenuItem onClick={() => createGroupWith(m.id)} data-testid={`ctx-new-group-${m.id}`}><Plus size={14} className="mr-2" /> New group…</ContextMenuItem>
                  </ContextMenuSubContent>
                </ContextMenuSub>
                <ContextMenuSub>
                  <ContextMenuSubTrigger data-testid={`ctx-color-${m.id}`}><Palette size={14} className="mr-2" /> Change colour</ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-60">
                    <div className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Just this person</div>
                    <div className="grid grid-cols-5 gap-1.5 px-1.5 pb-1">{PALETTE.map((c) => (<button key={c} onClick={() => setMemberColor([m.id], c)} data-testid={`ctx-swatch-self-${c}`} className="h-6 w-6 rounded-full ring-1 ring-black/10 transition-transform hover:scale-110" style={{ backgroundColor: c }} />))}</div>
                    <ContextMenuItem onClick={() => setColorPicker({ ids: [m.id], title: m.full_name })} data-testid={`ctx-more-colors-self-${m.id}`}><Palette size={14} className="mr-2" /> More colours…</ContextMenuItem>
                    <ContextMenuSeparator />
                    <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">This person + descendants</div>
                    <div className="grid grid-cols-5 gap-1.5 px-1.5 pb-1">{PALETTE.map((c) => (<button key={c} onClick={() => colorBranch(m, c)} data-testid={`ctx-swatch-branch-${c}`} className="h-6 w-6 rounded-full ring-1 ring-black/10 transition-transform hover:scale-110" style={{ backgroundColor: c }} />))}</div>
                    <ContextMenuItem onClick={() => setColorPicker({ ids: [m.id, ...descendantSet(members, m.id)], title: `${m.full_name}'s branch` })} data-testid={`ctx-more-colors-branch-${m.id}`}><Palette size={14} className="mr-2" /> More colours…</ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem onClick={() => setMemberColor([m.id], null)} data-testid={`ctx-color-reset-${m.id}`}><RotateCw size={14} className="mr-2" /> Reset this person</ContextMenuItem>
                    <ContextMenuItem onClick={() => colorBranch(m, null)} data-testid={`ctx-color-reset-branch-${m.id}`}><RotateCw size={14} className="mr-2" /> Reset person + descendants</ContextMenuItem>
                    {themes.length > 0 && (<>
                      <ContextMenuSeparator />
                      <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Apply saved theme to branch</div>
                      {themes.map((th) => (
                        <ContextMenuItem key={th.id} onClick={() => applyThemeToBranch(m.id, th)} onMouseEnter={() => setThemePreview({ map: themeBranchMap(m.id, th) })} onMouseLeave={() => setThemePreview(null)} data-testid={`ctx-theme-${th.id}-${m.id}`}>
                          <span className="mr-2 flex gap-0.5">{(th.colors || []).slice(0, 4).map((c, i) => (<span key={`${th.id}-${i}`} className="h-3 w-3 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c }} />))}</span>
                          {th.name}
                        </ContextMenuItem>
                      ))}
                    </>)}
                  </ContextMenuSubContent>
                </ContextMenuSub>
                <ContextMenuItem onClick={() => selectBranch(m)} data-testid={`ctx-select-branch-${m.id}`}><GitBranch size={14} className="mr-2" /> Select this person + descendants</ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={() => deleteNode(m)} data-testid={`ctx-delete-${m.id}`} className="text-red-600 focus:text-red-700"><Trash2 size={14} className="mr-2" /> Delete</ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          );
        })}
      </div>

      {exporting && (<div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60 backdrop-blur-sm" data-testid="export-overlay"><div className="rounded-full bg-white px-4 py-2 text-sm font-medium text-[#082D4A] shadow">Preparing your tree…</div></div>)}

      <TreeSearch hidden={presenting} value={search} onChange={setSearch} open={searchOpen} setOpen={setSearchOpen} matches={searchMatches} colorFor={colorFor} onPick={centerOn} />

      {pathMode && <PathBanner pathSel={pathSel} pathIds={pathIds} byId={byId} kinship={pathKinship} onClear={() => { setPathSel([]); setPathMode(false); }} />}
      {frameMode && <FrameModeHint onClose={() => setFrameMode(false)} />}
      {!pathMode && focusGroups.size > 0 && <FocusLegend groups={focusedGroups} onClear={() => setFocusGroups(new Set())} />}

      {showGroups && (
        <GroupsPanel groups={groups} focusGroups={focusGroups} canEdit={canEdit} onCreate={() => createGroupWith(null)} onToggleFocus={toggleFocusGroup}
          onApplyColor={applyGroupColor} onRename={renameGroup} onDelete={deleteGroupFn} onRecolor={recolorGroup} />
      )}

      {showMinimap && (
        <TreeMinimap vis={vis} disp={disp} CW={CW} CH={CH} colorBy={colorBy} colorFor={colorFor} t={t} contentRef={contentRef} viewportRef={viewportRef}
          mmRef={mmRef} mmDragRef={mmDragRef} navMinimap={navMinimap} hoverName={mmHover} onHover={setMmHover} onClose={() => setShowMinimap(false)} />
      )}

      <TreeControls
        presenting={presenting} canEdit={canEdit} isAdmin={isAdmin} editing={editing} locked={locked} homeId={homeId} showTour={tourPath.length >= 2 && !tour}
        bgUrl={bgUrl} bgFileRef={bgFileRef} onBgFile={onBgFile}
        nav={{ zoomCenter, fit, lost, showMinimap, setShowMinimap, jumpToMe, enterPresent, startTour,
          toggleLock: () => setLocked((v) => { if (!v) { setEditMode(false); stopTour(); } return !v; }),
          toggleEditMode: () => setEditMode((v) => !v) }}
        view={{ colorBy, setColorBy, showBands, setShowBands, showGroups, setShowGroups, showMinimap, setShowMinimap, pathMode, bloodlineOnly, setBloodlineOnly, theme, setTheme, orientation, setOrientation,
          togglePathMode: () => setPathMode((v) => { if (v) setPathSel([]); return !v; }), openThemes: () => setThemesOpen(true) }}
        edit={{ frameMode, resetLayout, clearBg, toggleFrameMode: () => { setFrameMode((v) => !v); setMultiSel(new Set()); }, openNamePacks: () => setNamePacksOpen(true), pickBackground: () => bgFileRef.current?.click() }}
        exp={{ exportTree, openLegendDialog, exportPrintTiled, exportFanChart }}
      />

      {tour && tourFocusId && <TourCaption tour={tour} focusId={tourFocusId} byId={byId} members={members} homeId={homeId} total={tourPath.length} />}
      {(presenting || tour) && (
        <PresenterBar tour={tour} total={tourPath.length} paused={tourPaused} voice={tourVoice} presenting={presenting} canStart={tourPath.length >= 2}
          onPrev={tourPrev} onNext={tourNext} onTogglePause={() => setTourPaused((p) => !p)} onStop={stopTour} onStart={startTour} onToggleVoice={() => setTourVoice((v) => !v)} onExit={exitPresent} />
      )}

      <QuickConnectDialog
        open={!!quickConnect}
        onClose={() => setQuickConnect(null)}
        anchor={quickConnect?.anchor || null}
        members={members}
        relation={quickConnect?.relation || null}
        existing={quickConnect?.existing || null}
        onConfirm={applyQuickConnect}
        onMerge={mergeMembers}
      />
      <NamePackDialog open={namePacksOpen} onOpenChange={setNamePacksOpen} onChanged={reloadNamePacks} />
      <ColorPickerDialog picker={colorPicker} colorFor={colorFor} onApply={setMemberColor} onClose={() => setColorPicker(null)} />
      <ThemesDialog open={themesOpen} onOpenChange={setThemesOpen} themes={themes} onSave={saveCurrentPalette} onDelete={deleteThemeFn} />
      <LegendExportDialog open={!!legendDialog} onClose={() => setLegendDialog(null)} title={legendTitle} setTitle={setLegendTitle} date={legendDate} setDate={setLegendDate} note={legendNote} setNote={setLegendNote} onExport={runLegendExport} />
    </div>
  );
}
